import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, ArrowRight } from "lucide-react";

/**
 * Weekly hero banner highlighting the highest-CTR partner from the last 7 days.
 * Renders nothing if no qualifying campaign exists. Tracks impressions + clicks
 * so the leaderboard reflects banner traffic too.
 */
export default function BrandOfTheWeek() {
    const [brand, setBrand] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const impressionFiredRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/brands/of-the-week");
                if (!cancelled) setBrand(data?.brand || null);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (brand && !impressionFiredRef.current) {
            impressionFiredRef.current = true;
            api.post(`/ads/${brand.campaign_id}/impression`).catch(() => { /* ignore */ });
        }
    }, [brand]);

    if (!brand || dismissed) return null;

    const onClick = () => {
        api.post(`/ads/${brand.campaign_id}/click`).catch(() => { /* ignore */ });
    };

    return (
        <div
            data-testid="feed-brand-of-week"
            className="relative bg-gradient-to-r from-midnight-pine via-deep-evergreen to-midnight-pine rounded-xl overflow-hidden p-5 lg:p-6 mb-6"
        >
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                {brand.image_url && (
                    <img src={brand.image_url} alt="" className="w-full h-full object-cover" />
                )}
            </div>
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest shrink-0">
                    <Trophy className="w-3 h-3" /> Brand of the week
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white font-display font-bold text-lg leading-tight truncate">
                        {brand.headline}
                    </div>
                    <div className="text-white/70 text-xs truncate mt-0.5">
                        {brand.name} · {brand.ctr}% CTR · {brand.impressions.toLocaleString()} impressions
                    </div>
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <Link
                        to={`/brands/${brand.user_id}`}
                        data-testid="botw-visit-brand"
                        onClick={onClick}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark active:scale-95 transition-all whitespace-nowrap"
                    >
                        Visit brand <ArrowRight className="w-3 h-3" />
                    </Link>
                    <button
                        data-testid="botw-dismiss"
                        onClick={() => setDismissed(true)}
                        title="Hide for now"
                        className="px-3 py-2 text-white/60 hover:text-white text-xs font-bold transition-colors"
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
}
